/**
 * Rule Library (404631) — every posting rule myLedger ships, in plain words, switched on per company.
 * Building a rule takes code knowledge; choosing which shipped ones apply does not. Accounts still come
 * from Posting Accounts, so each rule shows which of its accounts are mapped yet.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookMarked, CheckCircle2, XCircle, AlertTriangle, Power, PowerOff, RefreshCw } from 'lucide-react';

import { postingRuleApi, type RuleLibraryAreaDto, type RuleLibraryItemDto } from '@/api/postingRule.api';
import { roleLabel } from '@/api/roleAccount.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const STATUS: Record<RuleLibraryItemDto['status'], { label: string; variant: 'success' | 'default' | 'warning' }> = {
  ENABLED:     { label: 'Enabled',       variant: 'success' },
  UPDATE_AVAILABLE: { label: 'Enabled, newer version available', variant: 'warning' },
  DISABLED:    { label: 'Disabled',      variant: 'default' },
  NOT_ENABLED: { label: 'Not enabled',   variant: 'default' },
  EDITED:      { label: 'Edited, see Posting Rules', variant: 'warning' },
  CODE_IN_USE: { label: 'Code used by one of your own rules', variant: 'warning' },
};

const errorText = (e: unknown) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'The change could not be saved.';

export const RuleLibraryPage = () => {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Permissions.CFG_TRANSACTION_TYPE_CREATE);

  const [areas, setAreas]     = useState<RuleLibraryAreaDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState<string | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ rule: RuleLibraryItemDto; action: 'enable' | 'disable' | 'update' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setAreas(await postingRuleApi.library()); }
    catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const apply = async () => {
    if (!confirm) return;
    const { rule, action } = confirm;
    setBusy(rule.ruleCode);
    setError(null);
    try {
      await (action === 'enable' ? postingRuleApi.enableLibraryRule(rule.ruleCode)
        : action === 'update' ? postingRuleApi.updateLibraryRule(rule.ruleCode)
        : postingRuleApi.disableLibraryRule(rule.ruleCode));
      setConfirm(null);
      await load();
    } catch (e) {
      setError(errorText(e));
      setConfirm(null);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <BookMarked size={20} className="text-signal" /> Rule Library
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Ready-made posting rules for the documents your sales and stock systems send. Enable the ones you use;
          each posts to the accounts set in <Link to={AppRoutes.INTEGRATION_POSTING_ACCOUNTS} className="text-signal hover:underline">Posting Accounts</Link>.
        </p>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {error && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            <XCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : areas.map(area => (
          <section key={area.area}>
            <h2 className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              {area.area} <span className="normal-case font-normal text-slate-400">· {area.source === 'Any' ? 'any sender' : `sent by ${area.source}`}</span>
            </h2>
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
              {area.rules.map(rule => {
                const status = STATUS[rule.status];
                const missing = rule.accounts.filter(a => !a.mapped);
                const isOn = rule.status === 'ENABLED' || rule.status === 'UPDATE_AVAILABLE';
                return (
                  <div key={rule.ruleCode} className="px-4 py-3 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 dark:text-white">{rule.ruleName}</span>
                        <Badge variant={status.variant}>{status.label}</Badge>
                        <span className="font-mono text-[11px] text-slate-400">{rule.sourceEventType}</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{rule.summary}</p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-2">
                        {rule.accounts.map(a => (
                          <span key={a.role} title={a.mapped ? 'Mapped in Posting Accounts' : 'Not mapped yet — add it in Posting Accounts'}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${a.mapped
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                            {a.mapped ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {roleLabel(a.role)}
                          </span>
                        ))}
                        {rule.usesPaymentModes && (
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">+ an account for each payment mode</span>
                        )}
                      </div>
                      {isOn && missing.length > 0 && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">
                          Documents for this rule will be refused until {missing.map(m => roleLabel(m.role)).join(', ')} {missing.length === 1 ? 'is' : 'are'} mapped
                          in <Link to={AppRoutes.INTEGRATION_POSTING_ACCOUNTS} className="underline">Posting Accounts</Link>.
                        </p>
                      )}
                      {rule.otherActiveRules.length > 0 && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1.5 flex items-start gap-1">
                          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                          Also live for this event: {rule.otherActiveRules.join('; ')}. If this rule replaces it, suspend it in Posting Rules.
                        </p>
                      )}
                    </div>
                    {canManage && rule.status !== 'EDITED' && rule.status !== 'CODE_IN_USE' && (
                      <div className="flex flex-col gap-1.5 items-end">
                        {rule.status === 'UPDATE_AVAILABLE' && (
                          <Button size="sm" variant="primary" loading={busy === rule.ruleCode}
                            leftIcon={<RefreshCw size={14} />}
                            onClick={() => setConfirm({ rule, action: 'update' })}>
                            Update
                          </Button>
                        )}
                        <Button size="sm" variant={isOn ? 'outline' : 'primary'} loading={busy === rule.ruleCode}
                          leftIcon={isOn ? <PowerOff size={14} /> : <Power size={14} />}
                          onClick={() => setConfirm({ rule, action: isOn ? 'disable' : 'enable' })}>
                          {isOn ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!confirm}
        title={confirm?.action === 'enable' ? `Enable ${confirm.rule.ruleName}?`
          : confirm?.action === 'update' ? `Update ${confirm.rule.ruleName}?`
          : `Disable ${confirm?.rule.ruleName}?`}
        message={confirm?.action === 'enable'
          ? `Documents of type ${confirm.rule.sourceEventType} will post with this rule from now on, including any waiting to be retried.`
          : confirm?.action === 'update'
          ? `This release ships a newer version of this rule. Documents posted from now on, including any waiting to be retried, use the new version. Documents already posted are not changed.`
          : `Documents of type ${confirm?.rule.sourceEventType} will stop posting with this rule until it is enabled again.`}
        confirmLabel={confirm?.action === 'enable' ? 'Enable' : confirm?.action === 'update' ? 'Update' : 'Disable'}
        variant={confirm?.action === 'disable' ? 'warning' : 'info'}
        loading={!!busy}
        onConfirm={() => void apply()}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};
